import type { ReactElement, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import {
  isFirstCredentialStatusAction,
  RETRY_AFTER_MAX_SECONDS,
  RETRY_AFTER_MIN_SECONDS,
  type FirstCredentialStatusAction,
} from "@/features/first-credential/first-credential-client";
import { storeFirstCredentialHandoff } from "@/features/first-credential/first-credential-memory-handoff";
import { useFirstCredentialClient } from "@/features/first-credential/use-first-credential";
import { ClmIcon } from "@/shared/components/icons/icon-map";
import { SupportReference } from "@/shared/components/support-reference";
import { reportMemberSetPasswordSuppressed } from "@/shared/telemetry/safe-telemetry";
import { Alert, AlertDescription, AlertTitle, Button } from "@/shared/components/ui";
import {
  clearFirstCredentialProof,
  readFirstCredentialProof,
  type FirstCredentialProofOwner,
} from "./first-credential-proof-holder";

/**
 * CLMS-137 first-credential follow-up for `sign_in` completions and
 * already-completed re-entry. The follow-up action is resolved through the
 * NON-MINTING status operation (proof in the body only); while it resolves,
 * a non-actionable pending state renders — never the immediate primary
 * Sign in the addendum replaces — and a failed resolution maps to safe
 * re-entry/support guidance. Redemption runs only on explicit user
 * activation, transfers the only fc1 through the one-read in-memory
 * handoff, and navigates to a bearer-free /set-password URL; redemption
 * failures are announced with focus management instead of silently swapping
 * in a Sign in action, and an uncertain redemption response is TERMINAL for
 * this browser handoff (see the redeemUncertain branch). `no_action` (which
 * covers provider_unverified and other uncertainty) renders the
 * recovery-directed guidance without exposing a reason, retrying, or
 * offering redemption — with the caller's `recoveryEntry` (011-21 FR-050
 * proof-bound credential recovery) when provided. The client is called
 * directly with component-local state — never through the TanStack mutation
 * cache — so the proof and the minted fc1 stay out of every cache on this
 * surface (the terminal-state cache-eviction contract).
 */
type FollowUpResolution = "pending" | "status_failed" | FirstCredentialStatusAction;

/** Bounded explicit-retry budget for retryable (429/503) redemption outcomes. */
const REDEEM_RETRY_LIMIT = 3;

/**
 * Cold `no_action` recovery-directed guidance (011-15 UI contract L172):
 * generic, never a claim about whether a credential exists. Shared with the
 * registration page's `recovery_available` cold-ready terminal (clm-web#238
 * V5 / 011-21 addendum L60), which must render this row verbatim.
 */
export const RECOVERY_DIRECTED_GUIDANCE =
  "If you cannot sign in, use Forgot password or contact support.";

/**
 * Label of the caller-provided recovery entry (011-21 FR-050) and, per the
 * 2026-09-11 UI contract 17a amendment, the completion-neutral heading of
 * the registration page's `recovery_available` cold-ready terminal: the
 * page must never claim the registration is complete on that surface.
 */
export const SIGN_IN_TO_CONTINUE_LABEL = "Sign in to continue";

/**
 * Heading of the warm completion-phase `reconciliation_required` Support
 * terminal (011-21 addendum L64). The cold `support_required` terminal
 * must render byte-identical to it, so both read this one constant.
 */
export const RECONCILIATION_REQUIRED_HEADING = "We couldn't finish setting up your account";

/** Retryable pre-security-decision redemption context (authority unchanged). */
interface RedeemRetryableContext {
  kind: "rate_limited" | "dependency_unavailable";
  correlationId?: string;
  /** Absolute epoch-ms deadline for the Retry-After hold; 0 = no hold. */
  retryHoldUntil: number;
}

export function FirstCredentialFollowUp({
  proofOwner,
  recoveryEntry = null,
  memberContext = false,
}: {
  proofOwner: FirstCredentialProofOwner | null;
  /**
   * 011-21 FR-050: recovery-directed entry rendered by the `no_action`
   * guidance instead of the bare sign-in link. The proof stays held in the
   * module holder for the caller's proof-bound recovery start.
   */
  recoveryEntry?: ReactNode;
  /**
   * 011-21 FR-050 "Member completion and re-entry": a Member's
   * `set_password_available` status is treated as `no_action` (reported
   * through safe telemetry, no values) — never a credential CTA.
   */
  memberContext?: boolean;
}): ReactElement {
  const client = useFirstCredentialClient();
  const navigate = useNavigate();
  const [resolution, setResolution] = useState<FollowUpResolution>("pending");
  const [redeeming, setRedeeming] = useState(false);
  const [redeemUncertain, setRedeemUncertain] = useState(false);
  const [redeemDenied, setRedeemDenied] = useState<{ correlationId?: string } | null>(null);
  const [redeemRetryable, setRedeemRetryable] = useState<RedeemRetryableContext | null>(null);
  const [redeemRetryHeld, setRedeemRetryHeld] = useState(false);
  const [redeemRetryAttempted, setRedeemRetryAttempted] = useState(false);
  const statusStartedRef = useRef(false);
  // The proof never enters React state or props: it lives in the mutable
  // ownership-scoped module holder; this component receives only the
  // NON-SECRET ownership token, so no double-buffered fiber (current OR
  // alternate) can retain the secret after the synchronous scrub in
  // consumeProof — and a stale token can never touch a newer flow's proof.
  // Unmount/staleness guard: a redeem OR status promise settling after
  // unmount (or after a newer attempt) must not store fc1, mutate state,
  // or navigate.
  const activeRef = useRef(true);
  // The in-flight status request: aborted on unmount so a slow resolution
  // cannot outlive the surface (Copilot 3982728506); the abort also releases
  // the started latch so a StrictMode remount re-issues exactly one request.
  const statusAbortRef = useRef<AbortController | null>(null);
  const redeemAttemptRef = useRef(0);
  const redeemRetryableCountRef = useRef(0);
  // V13: focus moves to the retryable alert only on the null→non-null
  // transition, not on every failed-retry landing (each sets a fresh
  // context object).
  const redeemRetryableFocusedRef = useRef(false);
  const redeemUncertainAlertRef = useRef<HTMLDivElement | null>(null);
  const redeemDeniedAlertRef = useRef<HTMLDivElement | null>(null);
  const redeemRetryableAlertRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
      const controller = statusAbortRef.current;
      if (controller && !controller.signal.aborted) {
        controller.abort();
        statusAbortRef.current = null;
        statusStartedRef.current = false;
      }
    };
  }, []);

  // Terminal-state sensitive scrub: synchronously nulls the module holder —
  // the only place the proof lives — with this flow's own token (a stale
  // flow's clear is a no-op); the non-secret guidance keeps rendering.
  const consumeProof = useCallback((): void => {
    clearFirstCredentialProof(proofOwner);
  }, [proofOwner]);

  const hasRecoveryEntry = recoveryEntry !== null && recoveryEntry !== undefined;

  // Exactly one non-minting status resolution per mount; a failed
  // resolution maps to the safe re-entry/support guidance and is never
  // retried into a loop. The settlement callbacks carry the same staleness
  // guard as redemption: a resolution landing after unmount must not mutate
  // state or scrub a newer flow's holder.
  useEffect(() => {
    if (statusStartedRef.current) {
      return;
    }
    statusStartedRef.current = true;
    const proof = readFirstCredentialProof(proofOwner);
    if (!proof) {
      setResolution("status_failed");
      return;
    }
    const controller = new AbortController();
    statusAbortRef.current = controller;
    const isStale = (): boolean => controller.signal.aborted || !activeRef.current;
    client.status({ registrationProof: proof }, { signal: controller.signal }).then(
      (response) => {
        if (isStale()) {
          return;
        }
        // Defense in depth mirroring the client's fail-closed validator: an
        // action outside the closed contract set renders the safe
        // status_failed guidance — never an unsafe default CTA.
        let action: FollowUpResolution = isFirstCredentialStatusAction(response.action)
          ? response.action
          : "status_failed";
        if (memberContext && action === "set_password_available") {
          // 011-21 FR-050: a Member never gets a credential CTA on re-entry.
          reportMemberSetPasswordSuppressed();
          action = "no_action";
        }
        // sign_in / status_failed are terminal for the proof: no redemption
        // can follow, so the secret is cleared while the rendered guidance
        // stays. no_action keeps it held ONLY while a proof-bound recovery
        // entry can still consume it (011-21 FR-050).
        const keepsProof =
          action === "set_password_available" || (action === "no_action" && hasRecoveryEntry);
        if (!keepsProof) {
          consumeProof();
        }
        setResolution(action);
      },
      () => {
        if (isStale()) {
          return;
        }
        consumeProof();
        setResolution("status_failed");
      },
    );
  }, [client, consumeProof, proofOwner, memberContext, hasRecoveryEntry]);

  // Redemption-failure focus management: the activated control is replaced
  // (denied and uncertain are both terminal), so focus moves to the alert.
  useEffect(() => {
    if (redeemDenied) {
      redeemDeniedAlertRef.current?.focus();
    }
  }, [redeemDenied]);

  useEffect(() => {
    if (redeemUncertain) {
      redeemUncertainAlertRef.current?.focus();
    }
  }, [redeemUncertain]);

  useEffect(() => {
    if (!redeemRetryable) {
      redeemRetryableFocusedRef.current = false;
      return;
    }
    if (!redeemRetryableFocusedRef.current) {
      redeemRetryableFocusedRef.current = true;
      redeemRetryableAlertRef.current?.focus();
    }
  }, [redeemRetryable]);

  // Retry-After hold release: the hold itself is an absolute deadline set
  // atomically WITH the retryable context (no follow-up-effect frame where
  // the Try-again button renders enabled). When the timer fires it
  // re-checks the deadline and RESCHEDULES if time remains (a clock moved
  // backward must not release the hold early), clearing only at/after the
  // absolute deadline.
  useEffect(() => {
    if (!redeemRetryable) {
      return undefined;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const releaseAtDeadline = (): void => {
      const remainingMs = redeemRetryable.retryHoldUntil - Date.now();
      if (remainingMs <= 0) {
        setRedeemRetryHeld(false);
        return;
      }
      timer = setTimeout(releaseAtDeadline, Math.min(remainingMs, RETRY_AFTER_MAX_SECONDS * 1000));
    };
    releaseAtDeadline();
    return () => {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [redeemRetryable]);

  // Ownership transfer on explicit activation: the redeemed fc1 goes into
  // the one-read in-memory handoff, then navigation to the bearer-free
  // /set-password URL. Nothing sensitive touches the URL, history, storage,
  // or any cache.
  const handleRedeem = (): void => {
    const proof = readFirstCredentialProof(proofOwner);
    if (redeeming || !proof) {
      return;
    }
    // Absolute-deadline guard: the disabled attribute and the release timer
    // alone are not authoritative (a timer can fire before the deadline if
    // the clock moves backward), so redemption re-checks the hold itself.
    if (redeemRetryable && Date.now() < redeemRetryable.retryHoldUntil) {
      return;
    }
    setRedeeming(true);
    const attempt = ++redeemAttemptRef.current;
    const isCurrent = (): boolean => activeRef.current && attempt === redeemAttemptRef.current;
    client.redeem({ registrationProof: proof }).then(
      (result) => {
        if (!isCurrent()) {
          return;
        }
        if (result.outcome === "redeemed") {
          // Proof consumed just before the fc1 handoff: ownership transfers
          // to /set-password and nothing sensitive stays behind.
          consumeProof();
          storeFirstCredentialHandoff({
            authorization: result.authorization,
            expiresAt: result.expiresAt,
            passwordPolicy: result.passwordPolicy,
          });
          navigate("/set-password");
          return;
        }
        if (result.outcome === "rate_limited" || result.outcome === "dependency_unavailable") {
          // API contract retryable classes: 429 minted no proof; 503
          // occurred before the provider security decision with authority
          // unchanged. BOTH may carry an optional Retry-After (the OpenAPI
          // marks the header optional on each). Any advertised window
          // drives the same absolute-deadline hold; a response without one
          // allows an immediate explicit retry. The proof is kept for a
          // bounded, explicit user retry; past the budget the denial is
          // terminal.
          const retryAfterSeconds = result.retryAfterSeconds;
          const holdSeconds =
            typeof retryAfterSeconds === "number" &&
            Number.isInteger(retryAfterSeconds) &&
            retryAfterSeconds >= RETRY_AFTER_MIN_SECONDS &&
            retryAfterSeconds <= RETRY_AFTER_MAX_SECONDS
              ? retryAfterSeconds
              : null;
          const holdUsable = retryAfterSeconds === undefined || holdSeconds !== null;
          if (!holdUsable) {
            // Defense in depth mirroring the client's fail-closed Retry-After
            // validator: a retryable result whose advertised hold is unusable
            // is malformed and must not retain the proof or enable retries —
            // terminal for this browser handoff, like a thrown redeem.
            consumeProof();
            setRedeemRetryable(null);
            setRedeemUncertain(true);
            setRedeeming(false);
            return;
          }
          redeemRetryableCountRef.current += 1;
          if (redeemRetryableCountRef.current <= REDEEM_RETRY_LIMIT) {
            const retryHoldUntil = holdSeconds === null ? 0 : Date.now() + holdSeconds * 1000;
            // One batched commit: context, hold, and spinner reset land in
            // the same frame, and the initial hold derives from the deadline.
            setRedeemRetryable({
              kind: result.outcome,
              correlationId: result.correlationId,
              retryHoldUntil,
            });
            setRedeemRetryHeld(retryHoldUntil > Date.now());
            setRedeeming(false);
            return;
          }
        }
        // Safe non-success outcome: announced safe re-entry/support
        // guidance with the non-secret correlation reference — never a
        // silent swap to a Sign in action.
        consumeProof();
        setRedeemRetryable(null);
        setRedeemDenied({ correlationId: result.correlationId });
        setRedeeming(false);
      },
      () => {
        if (!isCurrent()) {
          return;
        }
        // Uncertain (network/thrown/malformed-response) failure: terminal
        // for this browser handoff — see the redeemUncertain branch below.
        consumeProof();
        setRedeemRetryable(null);
        setRedeemUncertain(true);
        setRedeeming(false);
      },
    );
  };

  // One persistent polite status region mounted across the pending→resolved
  // transition (WCAG 4.1.3, mirroring SupportPanel/RateLimitPanel): mounting
  // a live region WITH its content never announces, so the readiness text
  // must be an update to an already-mounted region. Failure paths announce
  // through their focused role="alert" surfaces instead, so the region goes
  // quiet for them (no double announcement).
  const pending = resolution === "pending";
  const readinessStatus = ((): string | null => {
    if (redeemDenied || redeemUncertain || redeemRetryable || resolution === "status_failed") {
      return null;
    }
    if (resolution === "set_password_available") {
      return "You can set your password now.";
    }
    if (resolution === "sign_in") {
      return "You can sign in now.";
    }
    if (resolution === "no_action") {
      // Restates the visible recovery-directed guidance (011-15 UI contract
      // L172): generic, never a claim about whether a credential exists.
      return RECOVERY_DIRECTED_GUIDANCE;
    }
    return null;
  })();

  return (
    <div className="mt-4">
      <p
        role="status"
        className={pending ? "flex items-center gap-2 text-sm text-muted-foreground" : "sr-only"}
      >
        {pending ? (
          <>
            <ClmIcon
              name="loading"
              decorative
              className="h-5 w-5 animate-spin text-clm-brand-blue motion-reduce:animate-none"
            />
            <span>Checking your next step</span>
          </>
        ) : (
          readinessStatus
        )}
      </p>
      {renderResolved()}
    </div>
  );

  function renderResolved(): ReactElement | null {
    if (redeemDenied) {
      return (
        <div>
          <Alert ref={redeemDeniedAlertRef} tabIndex={-1} variant="warning">
            <AlertTitle>We can&apos;t start password setup right now</AlertTitle>
            <AlertDescription>
              Your registration is still complete and nothing was undone. Open the link from your
              registration email to try again later, or contact support.
            </AlertDescription>
          </Alert>
          <Button asChild className="mt-3 w-full">
            <Link to="/login">Sign in / use password recovery</Link>
          </Button>
          <SupportReference
            label="Password setup reference"
            correlationId={redeemDenied.correlationId}
          />
        </div>
      );
    }

    if (redeemUncertain) {
      // TERMINAL for this browser handoff: redemption consumes the
      // single-use bootstrap server-side BEFORE the response returns, so
      // after a lost/uncertain response we cannot know whether the fc1 was
      // already minted and lost. A direct redeem retry would replay against
      // an already-consumed bootstrap and mislead the user, and any
      // "nothing was changed" claim would be false. The only safe path is
      // proof-bound re-entry (the registration email link), which re-checks
      // status non-mintingly. The parent completed/already-completed panel
      // keeps its SupportReference correlation reference on screen below.
      return (
        <Alert ref={redeemUncertainAlertRef} tabIndex={-1} variant="warning">
          <AlertTitle>We couldn&apos;t start password setup</AlertTitle>
          <AlertDescription>
            We didn&apos;t get a response, so we can&apos;t confirm whether password setup started.
            Open the link from your registration email to check your next step, or contact support.
          </AlertDescription>
        </Alert>
      );
    }

    if (redeemRetryable) {
      // Retryable pre-security-decision classes (API contract): the proof
      // and the bootstrap are intact, so a bounded explicit retry replaces
      // the terminal denial — held for the Retry-After window on 429.
      // Sibling parity (SupportPanel/RateLimitPanel/RetryablePanel): the
      // persistent polite status region and the Try-again control render
      // OUTSIDE the Alert — nesting role="status" inside the implicit
      // role="alert" (assertive + atomic) would re-announce the whole alert
      // on every retrying/still-blocked flip.
      const rateLimited = redeemRetryable.kind === "rate_limited";
      return (
        <div>
          <Alert ref={redeemRetryableAlertRef} tabIndex={-1} variant="warning">
            <AlertTitle>
              {rateLimited
                ? "Too many attempts right now"
                : "Password setup is temporarily unavailable"}
            </AlertTitle>
            <AlertDescription>
              <p>
                {rateLimited
                  ? "Please wait a moment, then try again. Nothing was changed."
                  : "Nothing was changed. It is safe to try again in a moment."}
              </p>
            </AlertDescription>
          </Alert>
          {/* A retry that lands on this same panel re-renders it, so this
              persistent polite region is the only assistive-technology
              feedback that the retry ran (WCAG 4.1.3). */}
          <p role="status" className="mt-2 text-sm text-gray-600">
            {redeeming
              ? "Retrying\u2026"
              : redeemRetryAttempted
                ? rateLimited
                  ? "Still too many attempts. Please wait a moment before trying again."
                  : "Password setup is still unavailable. Try again later."
                : null}
          </p>
          <Button
            type="button"
            variant="outline"
            className="mt-4"
            onClick={() => {
              setRedeemRetryAttempted(true);
              handleRedeem();
            }}
            disabled={redeemRetryHeld || redeeming}
          >
            Try again
          </Button>
          <SupportReference
            label="Password setup reference"
            correlationId={redeemRetryable.correlationId}
          />
        </div>
      );
    }

    if (resolution === "set_password_available") {
      return (
        <div>
          <p className="text-base text-gray-600">
            Setting your password is a secure, time-limited step. We&apos;ll never send you a
            password by email.
          </p>
          <Button className="mt-3 w-full" onClick={handleRedeem} disabled={redeeming}>
            Set your password
          </Button>
        </div>
      );
    }

    if (resolution === "no_action") {
      // Recovery-directed standard sign-in surface: no reason, no retry, no
      // redemption offer. With a caller-provided recovery entry (011-21
      // FR-050) that entry IS the sign-in path; otherwise the sanctioned
      // Hosted-UI-owned recovery affordance is a real CTA into /login.
      return (
        <div>
          <p className="text-base text-gray-600">{RECOVERY_DIRECTED_GUIDANCE}</p>
          {hasRecoveryEntry ? (
            recoveryEntry
          ) : (
            <Button asChild className="mt-3 w-full">
              <Link to="/login">Sign in / use password recovery</Link>
            </Button>
          )}
        </div>
      );
    }

    if (resolution === "status_failed") {
      // Safe re-entry/support guidance — never a bare Sign in that could
      // send a credential-less member into the Hosted UI dead end.
      return (
        <Alert variant="warning">
          <AlertTitle>We can&apos;t confirm your next step right now</AlertTitle>
          <AlertDescription>
            Your registration is complete and nothing was lost. Open the link from your registration
            email to try again later, or contact support.
          </AlertDescription>
        </Alert>
      );
    }

    if (resolution === "pending") {
      // Non-actionable pending state: the persistent status region above is
      // the whole surface; no primary action renders until the status
      // operation resolves what this member can safely do.
      return null;
    }

    // Resolved sign_in action: the credential is established, so the
    // standard Sign in action is safe.
    return (
      <Button asChild className="w-full">
        <Link to="/login">Sign in</Link>
      </Button>
    );
  }
}
